/**
 * live-news-youtube-streams.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * All YouTube-based live news sources.
 *
 * HOW TO UPDATE:
 *  - `url`       : Last-known direct watch URL (watch?v=VIDEO_ID).
 *                  Used as Tier-2 fallback when the backend proxy is unavailable.
 *  - `channelId` : The YouTube Channel ID.
 *                  • Tier-1 fallback: backend calls /api/youtube/live/{channelId}
 *                    which reads the RSS feed → returns the current live video ID.
 *                  • Tier-3 fallback: live_stream?channel={channelId} embed —
 *                    YouTube always serves whichever video is currently live.
 *
 * To find a channelId:
 *   Open channel page → View Source → search for "channelId" or "externalId"
 *   Or grab it from the URL: youtube.com/channel/UC...
 * ─────────────────────────────────────────────────────────────────────────────
 */

import type { NewsSource } from "./live-news-types"

// ── US ───────────────────────────────────────────────────────────────────────
// ── US ───────────────────────────────────────────────────────────────────────
export const usYouTubeSources: NewsSource[] = []

// ── Canada ───────────────────────────────────────────────────────────────────
export const canadaYouTubeSources: NewsSource[] = [
    {
        name: "CBC News",
        url: "https://www.youtube.com/watch?v=5vfaDsMhCF4",
        type: "youtube",
        channelId: "UCuVZGpzfk5b0p2OIfbU0yFA", // Fallback channel ID for automation if watch ID expires
        region: "Canada",
        icon: "https://www.google.com/s2/favicons?domain=cbc.ca&sz=32",
        epgId: "CBCNews.ca"
    },
];

// ── Business ─────────────────────────────────────────────────────────────────
export const businessYouTubeSources: NewsSource[] = [
    {
        name: "Schwab Network",
        url: "https://www.youtube.com/watch?v=XOia1uhjisY",
        type: "youtube",
        channelId: "UCqoSrYgusd8ZddtMoWhjHYA",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=schwab.com&sz=32",
        epgId: "SchwabNetwork.us"
    },
    {
        name: "CNBC Marathon",
        url: "https://www.youtube.com/watch?v=9NyxcX3rhQs",
        type: "youtube",
        region: "Business",
        icon: "https://www.google.com/s2/favicons?domain=cnbc.com&sz=32",
        epgId: "CNBCMarathon.us"
    },
]

// ── UK ───────────────────────────────────────────────────────────────────────
export const ukYouTubeSources: NewsSource[] = []

// ── Europe ───────────────────────────────────────────────────────────────────
export const europeYouTubeSources: NewsSource[] = []

// ── Middle East ──────────────────────────────────────────────────────────────
export const middleEastYouTubeSources: NewsSource[] = [
    {
        name: "i24 News",
        url: "https://www.youtube.com/watch?v=1bvwjI3FkEU", // Fallback VOD/Live URL
        type: "youtube",
        channelId: "UCvHDpsWKADrDia0c99X37vg",
        region: "Middle East",
        icon: "https://www.google.com/s2/favicons?domain=i24news.tv&sz=32",
        epgId: "i24News.us"
    },
]

// ── India ────────────────────────────────────────────────────────────────────
export const indiaYouTubeSources: NewsSource[] = []

// ── Singapore ────────────────────────────────────────────────────────────────
export const singaporeYouTubeSources: NewsSource[] = []

// ── Taiwan ───────────────────────────────────────────────────────────────────
export const taiwanYouTubeSources: NewsSource[] = [
    {
        name: "TaiwanPlus",
        url: "https://www.youtube.com/watch?v=Vrs-AeKZIEg",
        type: "youtube",
        channelId: "UC7c6rvyAZLpKGk8ttVnpnLA",
        region: "Taiwan",
        icon: "https://www.google.com/s2/favicons?domain=taiwanplus.com&sz=32",
        epgId: "TaiwanPlus.tw"
    },
]

// ── South Korea ──────────────────────────────────────────────────────────────
export const southKoreaYouTubeSources: NewsSource[] = [
    {
        name: "YTN",
        url: "https://www.youtube.com/watch?v=goAg7E_k9bQ",
        type: "youtube",
        channelId: "UChlgI3UHCOnwUGzWzbJ3H5w",
        region: "South Korea",
        icon: "https://www.google.com/s2/favicons?domain=ytn.co.kr&sz=32",
        epgId: "YTN.kr"
    },
]

// ── Japan ────────────────────────────────────────────────────────────────────
export const japanYouTubeSources: NewsSource[] = [
    {
        name: "TBS News DIG",
        url: "https://www.youtube.com/watch?v=Anr15FA9OCI",
        type: "youtube",
        region: "Japan",
        icon: "https://www.google.com/s2/favicons?domain=newsdig.tbs.co.jp&sz=32",
        epgId: "TBSNewsDig.jp"
    },
    {
        name: "ANN News",
        url: "https://www.youtube.com/watch?v=coYw-eVU0Ks",
        type: "youtube",
        channelId: "UCGCZAYq5Xxojl_tSXcVJhiQ",
        region: "Japan",
        icon: "https://www.google.com/s2/favicons?domain=news.tv-asahi.co.jp&sz=32",
        epgId: "ANNNews.jp"
    },
]

// ── Australia ────────────────────────────────────────────────────────────────
export const australiaYouTubeSources: NewsSource[] = []

// ── All YouTube sources combined ─────────────────────────────────────────────
export const allYouTubeSources: NewsSource[] = [
    ...usYouTubeSources,
    ...canadaYouTubeSources,
    ...businessYouTubeSources,
    ...ukYouTubeSources,
    ...europeYouTubeSources,
    ...middleEastYouTubeSources,
    ...indiaYouTubeSources,
    ...singaporeYouTubeSources,
    ...taiwanYouTubeSources,
    ...southKoreaYouTubeSources,
    ...japanYouTubeSources,
    ...australiaYouTubeSources,
]
